import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { articlePath } from '../journal/journalRules';
import { useCatalog } from '../../contexts/CatalogContext';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { HOME_ARTICLES } from './homeRules';

/** Hoe snel het zwevende beeld de muis volgt. */
const FOLLOW = {
  stiffness: 380,
  damping: 32,
  mass: 0.6
};

/**
 * Journal als tijdschrift: het nieuwste artikel groot met beeld (zoomt zacht bij aanwijzen),
 * de rest als rustige rijen. Wijs je met de muis een rij aan, dan zweeft het beeld van dat
 * artikel mee met je cursor, als een polaroid boven het blad. Alleen op computers met muis,
 * en niet bij "minder beweging"; het is een extraatje, de rij zelf zegt alles al.
 */
export function HomeJournal() {
  const pages = usePages();
  const {
    articles
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.journal;
  const [lead, ...rest] = articles.slice(0, HOME_ARTICLES);
  const [hovered, setHovered] = useState<string | null>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, FOLLOW);
  const y = useSpring(my, FOLLOW);
  if (!lead) return null;

  // Alleen een voorbeeld met een echt beeld; `previewImage` is dan altijd een niet-lege tekst.
  const preview = rest.find(a => a.slug === hovered && a.image);
  const previewImage = preview?.image || null;
  const onMove = (e: MouseEvent<HTMLUListElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - box.left);
    my.set(e.clientY - box.top);
  };
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-journal" data-id="element-7380" data-mp="n01mvn0ndu1o">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7381" data-mp="iyomhsgbqj25">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7382" data-mp="izht8tfa98ka">
          <h2 id="home-journal" className={`max-w-2xl ${sectionHeading} text-ink`} data-id="element-7383" data-mp="3rfjay2wdk0wp">
            <HeadingText heading={copy.heading} data-id="element-7384" data-mp="3mg6qtpww17xt" />
          </h2>
          {copy.cta && <Link to="/journal" className={`${quietLink} text-ink`} data-id="element-7385" data-mp="4ablui6akc1b">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7386" data-mp="3vflekhlo74em" />
            </Link>}
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7387" data-mp="1fnnvxasn9qvx">
          <article className="lg:col-span-7" data-id="element-7388" data-mp="1aqnlknori1z0">
            <Link to={articlePath(lead)} className="group block" data-id="element-7389" data-mp="1apm6m84mkvc0">
              <div className="overflow-hidden" data-id="element-7390" data-mp="1j5tks485r5df">
                <MediaView media={{
                image: lead.image || founder.portraitPalm,
                alt: ''
              }} sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[4/3] w-full transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-7391" data-mp="1j992zfx4ouj2" />
              </div>
              <p className="mt-6 text-[13px] text-clay" data-id="element-7392" data-mp="21wujcr5d8ywg">
                {lead.category} · {lead.readTime}
              </p>
              <h3 className="mt-2 font-display text-[2rem] font-light leading-[1.1] text-ink transition-colors duration-150 ease-soft group-hover:text-clay sm:text-[2.4rem]" data-id="element-7393" data-mp="26vfliw46hsnj">
                {lead.title}
              </h3>
              <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-muted" data-id="element-7394" data-mp="1x4tbdhf8duc8">{lead.excerpt}</p>
            </Link>
          </article>

          {rest.length > 0 && <ul onMouseMove={reduce ? undefined : onMove} onMouseLeave={() => setHovered(null)} className="relative divide-y divide-ink/15 border-y border-ink/15 lg:col-span-5 lg:self-start" data-id="element-7395" data-mp="2po09q08xn9l0">
              {rest.map(a => <li key={a.slug} data-id="element-7396" data-mp="rzxscylpj9po">
                  <Link to={articlePath(a)} onMouseEnter={() => setHovered(a.slug)} className="group grid grid-cols-[1fr_auto] gap-4 py-7" data-id="element-7397" data-mp="s0zf40c35kn8">
                    <span data-id="element-7398" data-mp="1kr7nx1kqesp5">
                      <span className="block text-[13px] text-clay" data-id="element-7399" data-mp="1fqa98yyxkgse">{a.category}</span>
                      <span className="mt-2 block font-display text-[1.5rem] font-light leading-snug text-ink transition-[color,transform] duration-200 ease-soft group-hover:translate-x-1 group-hover:text-clay" data-id="element-7400" data-mp="1azbq7rmk54lg">
                        {a.title}
                      </span>
                      <span className="mt-2 block text-[15px] leading-relaxed text-muted" data-id="element-7401" data-mp="23gxjvs08dkin">{a.excerpt}</span>
                    </span>
                    <ArrowUpRightIcon className="mt-1 h-5 w-5 text-ink/40 transition-[color,transform] duration-200 ease-soft group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-7402" data-mp="260amxfckhfoz" />
                  </Link>
                </li>)}

              {!reduce && <AnimatePresence data-id="element-7403" data-mp="2ozoks8df3akr">
                  {preview && previewImage && <motion.div key={preview.slug} aria-hidden="true" style={{
              x,
              y
            }} initial={{
              opacity: 0,
              scale: 0.96,
              rotate: -2
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: -4
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.18,
              ease
            }} className="pointer-events-none absolute left-0 top-0 z-raised hidden lg:block" data-id="element-7404" data-mp="2txhqz1eq04l1">
                      <div className="-translate-x-[115%] -translate-y-1/2 bg-shell p-2 shadow-[0_18px_40px_-16px_rgba(43,36,32,0.45)]" data-id="element-7405" data-mp="1dkiwaqnmckzo">
                        <ProductImage src={previewImage} alt="" fit="cover" width={176} className="h-[13rem] w-44" data-id="element-7406" data-mp="18itjb5gu5x8y" />
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </ul>}
        </div>

        {copy.askLine && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`mt-12 ${quietLink} text-muted hover:text-ink`} data-id="element-7407" data-mp="1rb4e0c1c6ovz">
            <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7408" data-mp="25b6sdoafr06h" />
            {fill(copy.askLine)}
            <span className="sr-only" data-id="element-7409" data-mp="21a3eh7by7e7w">{shopLabels.viaWhatsApp}</span>
          </a>}
      </div>
    </section>;
}